// The generic setter for a world pass's own uniforms (R431).
//
// Every map pass of `shaders/` publishes a `UniformSpec[]`: the name, the
// GLSL type and the default the data layer gave it. That list is the
// contract — `shaders/shaders.test.ts` pins that the source declares
// exactly the names the list carries — so the wiring must not restate it as
// a hand-written setter per pass. A hand-written one drifts: a uniform
// added to a shader compiles, links, reads whatever the driver left in it,
// and nobody sees the mistake until the picture is wrong.
//
// So a layer builds ONE `UniformValues` from its pass's list, writes over
// the entries a knob or a frame moves, and hands the whole set to the
// program on every draw. A shader that dropped a uniform costs one map
// lookup that finds no location and returns (`Pipeline` skips a null
// location), so the set is safe to over-state and can never under-state.
//
// The A.6 shared half — the samplers, the palette, the grid, the camera and
// the clock — is NOT here. `gfx/worldquad.ts` writes it, and it is the one
// place that writes it.

import type { UniformSpec, UniformType } from '../shaders/prelude';
import type { UniformSink } from './types';

/** A value a uniform can carry: one number, or the flattened components. */
export type UniformValue = number | readonly number[];

/** Is the type written with a `*v` setter, from a `Float32Array`? */
export function isArrayType(t: UniformType): boolean {
  return t === 'float[]' || t === 'vec3[]' || t === 'vec4[]' || t === 'mat3';
}

/**
 * The live value of every uniform one pass owns.
 *
 * It allocates once. `set` writes an entry and `apply` writes them all to
 * the bound program; neither allocates, because every array uniform keeps a
 * `Float32Array` of its own from construction.
 */
export class UniformValues {
  /** One slot a uniform, by name. */
  private readonly slots = new Map<string, Slot>();
  /**
   * The same slots in the order of the pass's list. `apply` walks it by
   * index, because a walk of the map makes one entry pair a uniform a draw.
   */
  private readonly order: Slot[] = [];

  constructor(specs: readonly UniformSpec[]) {
    for (const spec of specs) {
      // A sampler is bound by the world pass, which owns the unit table.
      if (spec.type === 'sampler') continue;
      const array = isArrayType(spec.type);
      const slot: Slot = {
        name: spec.name,
        type: spec.type,
        array,
        value: spec.value,
        buf: array && typeof spec.value !== 'number'
          ? new Float32Array(spec.value.length) : null,
      };
      // A name the list states twice keeps its first place and its last
      // slot, as the map alone kept them.
      const had = this.slots.get(spec.name);
      if (had) this.order[this.order.indexOf(had)] = slot;
      else this.order.push(slot);
      this.slots.set(spec.name, slot);
    }
  }

  /** Does the set carry this uniform? A name it does not carry is a typo. */
  has(name: string): boolean { return this.slots.has(name); }

  /**
   * Writes one uniform's value. It refuses a name the pass never declared,
   * because a silent miss is a knob that does nothing and a whole evening
   * spent on finding out why.
   */
  set(name: string, value: UniformValue): void {
    const slot = this.slots.get(name);
    if (!slot) throw new Error(`fast: ${name} is not a uniform of this pass`);
    slot.value = value;
  }

  /** Reads one uniform's value back. For the tests and the debug handle. */
  get(name: string): UniformValue | undefined { return this.slots.get(name)?.value; }

  /** Writes every uniform to the bound program. */
  apply(p: UniformSink): void {
    for (let i = 0; i < this.order.length; i++) {
      const slot = this.order[i];
      const name = slot.name;
      const type = slot.type;
      const v = slot.value;
      if (slot.array && typeof v !== 'number') {
        let buf = slot.buf;
        if (!buf || buf.length !== v.length) buf = slot.buf = new Float32Array(v.length);
        buf.set(v);
        switch (type) {
          case 'float[]': p.uniform1fv(name, buf); break;
          case 'vec3[]': p.uniform3fv(name, buf); break;
          case 'vec4[]': p.uniform4fv(name, buf); break;
          case 'mat3': p.uniformMatrix3fv(name, buf); break;
        }
        continue;
      }
      const n = typeof v === 'number' ? v : 0;
      const a = typeof v === 'number' ? EMPTY : v;
      switch (type) {
        case 'float': p.uniform1f(name, n); break;
        case 'int': p.uniform1i(name, n); break;
        case 'vec2': p.uniform2f(name, a[0] ?? 0, a[1] ?? 0); break;
        case 'vec3': p.uniform3f(name, a[0] ?? 0, a[1] ?? 0, a[2] ?? 0); break;
        case 'vec4': p.uniform4f(name, a[0] ?? 0, a[1] ?? 0, a[2] ?? 0, a[3] ?? 0); break;
        case 'ivec2': p.uniform2i(name, a[0] ?? 0, a[1] ?? 0); break;
      }
    }
  }
}

const EMPTY: readonly number[] = [];

/** The type, the live value and the kept upload array of one uniform. */
interface Slot {
  readonly name: string;
  readonly type: UniformType;
  /** Is the type written with a `*v` setter? Read once, not per apply. */
  readonly array: boolean;
  value: UniformValue;
  buf: Float32Array | null;
}
